// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import { useAppDispatch } from '../../redux/app/hooks';
import { addAlert } from '../../redux/features/app/appSlice';
import { AuthHelper } from '../auth/AuthHelper';
import { AlertType } from '../models/AlertType';

import { getErrorDetails } from '../../components/utils/TextUtils';
import { ChatArchiveService } from '../services/ChatArchiveService';

export const useArchive = () => {
    const dispatch = useAppDispatch();
    const { instance, inProgress } = useMsal();

    const chatArchiveService = new ChatArchiveService();

    const loadChats = async (from: Date, to: Date) => {
        try {
            const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
            const chatSessions = await chatArchiveService.listAsync(from, to, accessToken);

            return chatSessions;
        } catch (e: any) {
            const errorMessage = `Unable to load chats. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));

            return false;
        }
    };

    const loadMessages = async (chatId: string) => {
        try {
            const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
            const messages = await chatArchiveService.getMessagesAsync(chatId, accessToken);

            return messages;
        } catch (e: any) {
            const errorMessage = `Unable to load messages. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));

            return false;
        }
    };

    const downloadListExport = async (from: Date, to: Date) => {
        try {
            const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
            const blob = await chatArchiveService.downloadListExcel(from, to, accessToken);

            return blob;
        } catch (e: any) {
            const errorMessage = `Unable to create export. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));

            return false;
        }
    };

    return {
        loadChats,
        loadMessages,
        downloadListExport,
    };
};
